/*
  Two-way sync between the main product buy box (Horizon theme
  block-variant-picker / quantity-selector) and the sticky add-to-cart
  bar (SMI_StickyAddToCart1).

  Both sides are driven through their own existing change pipelines
  instead of duplicating price/media/availability logic:
  - Variant sync replays the target variant's option values onto the
    main variant picker's real inputs and fires a native "change", so
    block-variant-picker's own handler (price, media, inventory, add
    button) runs exactly as it would for a user click.
  - Sticky's variant update goes through the "smiVariantChange" event
    the sticky bundle already listens for.
  - Quantity sync mirrors values in both directions with equality
    checks instead of sync flags, so mutual event listeners settle
    without an explicit lock.
*/
function initStickyAtcSync() {
  const stickySection = document.querySelector('.smi-sticky-add-to-cart-1')
  if (!stickySection) return

  const stickySelect = stickySection.querySelector('.smi-product-form select')
  const stickyQtyInput = stickySection.querySelector('._input-quantity')
  const stickyMinus = stickySection.querySelector('._input-minus')
  const stickyPlus = stickySection.querySelector('._input-plus')

  const mainBuyButtons = document.querySelector('block-buy-buttons')
  const mainVariantPicker = document.querySelector('block-variant-picker')
  const mainQuantitySelector = mainBuyButtons && mainBuyButtons.querySelector('quantity-selector')
  const mainSectionId = mainBuyButtons && mainBuyButtons.dataset.sectionId

  setUpBodyVisibilityClass(stickySection)

  setUpStickyPriceMirror(stickySection, mainSectionId)
  setUpVariantSync(mainVariantPicker, stickySelect)
  setUpQuantitySync(mainQuantitySelector, stickyQtyInput, stickyMinus, stickyPlus)
  setUpAddToCartDelegation(stickySection)
}

const STICKY_VISIBLE_BODY_CLASS = 'obx-sticky-atc-visible'

function isStickyHidden(transform) {
  return transform === '' || transform === 'translateY(100%)'
}

function setUpBodyVisibilityClass(stickySection) {
  const syncBodyClass = () => {
    const isVisible = !isStickyHidden(stickySection.style.transform)
    document.body.classList.toggle(STICKY_VISIBLE_BODY_CLASS, isVisible)
  }

  syncBodyClass()
  new MutationObserver(syncBodyClass).observe(stickySection, {
    attributes: true,
    attributeFilter: ['style']
  })
}

function setUpAddToCartDelegation(stickySection) {
  const stickyForm = stickySection.querySelector('smi-product-form form')
  const stickyButton = stickySection.querySelector('._add-to-cart--1')
  if (!stickyForm || !stickyButton) return

  document.addEventListener(
    'submit',
    (event) => {
      if (event.target !== stickyForm) return

      const mainAddButton = document.querySelector('block-buy-buttons [name="add"]')

      if (!mainAddButton || mainAddButton.disabled) return

      event.preventDefault()
      event.stopImmediatePropagation()
      delegateAddToCart(mainAddButton, stickyButton)
    },
    true
  )
}

function delegateAddToCart(mainAddButton, stickyButton) {
  stickyButton.classList.add('_loading')
  stickyButton.setAttribute('aria-disabled', 'true')

  const clearLoading = () => {
    stickyButton.classList.remove('_loading')
    stickyButton.removeAttribute('aria-disabled')
  }

  document.addEventListener('ajaxProduct:added', clearLoading, { once: true })
  document.addEventListener('ajaxProduct:error', clearLoading, { once: true })

  setTimeout(clearLoading, 8000)

  mainAddButton.click()
}

function setUpStickyPriceMirror(stickySection, mainSectionId) {
  if (!mainSectionId) return

  const priceEl = stickySection.querySelector('.smi-sticky-price')
  const mainPrice = document.querySelector(`block-price[data-section-id="${mainSectionId}"]`)
  if (!priceEl || !mainPrice) return

  const mirror = () => {
    priceEl.innerHTML = mainPrice.innerHTML
  }

  mirror()
  new MutationObserver(mirror).observe(mainPrice, {
    childList: true,
    subtree: true,
    characterData: true
  })
}

function setUpVariantSync(mainVariantPicker, stickySelect) {
  if (!mainVariantPicker || !stickySelect) return

  const eventName = `variant:change:${mainVariantPicker.dataset.sectionId}:${mainVariantPicker.dataset.productId}`
  let variantData = null

  function getMainVariants() {
    if (!variantData) {
      const script = mainVariantPicker.querySelector('script[type="application/json"]')
      variantData = script ? JSON.parse(script.textContent) : []
    }
    return variantData
  }

  function escapeValue(value) {
    return window.CSS && CSS.escape ? CSS.escape(value) : value.replace(/["\\]/g, '\\$&')
  }

  // Main -> sticky
  document.addEventListener(eventName, (event) => {
    const variant = event.detail && event.detail.variant
    if (!variant || String(stickySelect.value) === String(variant.id)) return

    document.dispatchEvent(new CustomEvent('smiVariantChange', { detail: { id: variant.id } }))
  })

  // Sticky -> main
  stickySelect.addEventListener('change', () => {
    const target = getMainVariants().find((variant) => String(variant.id) === String(stickySelect.value))
    if (!target) return

    const optionInputs = Array.from(mainVariantPicker.querySelectorAll('select, fieldset'))
    let changedInput = null

    optionInputs.forEach((wrapper, index) => {
      const optionValue = target[`option${index + 1}`]
      if (optionValue == null) return

      if (wrapper.tagName === 'SELECT') {
        if (wrapper.value !== optionValue) {
          wrapper.value = optionValue
          changedInput = wrapper
        }
      } else {
        const radio = wrapper.querySelector(`input[value="${escapeValue(optionValue)}"]`)
        if (radio && !radio.checked) {
          radio.checked = true
          changedInput = radio
        }
      }
    })

    if (changedInput) changedInput.dispatchEvent(new Event('change', { bubbles: true }))
  })
}

function setUpQuantitySync(mainQuantitySelector, stickyQtyInput, stickyMinus, stickyPlus) {
  if (!mainQuantitySelector || !stickyQtyInput) return

  const mainQtyInput = mainQuantitySelector.querySelector('.js-qty__num')
  if (!mainQtyInput) return

  // Main -> sticky
  mainQuantitySelector.addEventListener('quantity:change', (event) => {
    const qty = event.detail.qty
    if (String(stickyQtyInput.value) === String(qty)) return

    stickyQtyInput.value = qty
    if (stickyMinus) stickyMinus.classList.toggle('_disable', qty <= 1)
  })

  // Sticky -> main
  function pushStickyQtyToMain() {
    const qty = parseInt(stickyQtyInput.value, 10) || 1
    if (String(mainQtyInput.value) === String(qty)) return

    mainQtyInput.value = qty
    mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }))
  }

  // Vendor plus/minus handlers mutate the input's value property directly
  // (no input/change event), so read the result on the next tick.
  const pushOnNextTick = () => setTimeout(pushStickyQtyToMain, 0)

  if (stickyPlus) stickyPlus.addEventListener('click', pushOnNextTick)
  if (stickyMinus) stickyMinus.addEventListener('click', pushOnNextTick)
  stickyQtyInput.addEventListener('change', pushStickyQtyToMain)
}

if (!window.__stickyAtcSyncInitialized) {
  window.__stickyAtcSyncInitialized = true

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickyAtcSync)
  } else {
    initStickyAtcSync()
  }
}